import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { type CatalogAdminView, type ComboView, useCatalogMutation } from "../../hooks/catalog";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { localName, money } from "../../lib/format";
import { Empty, Pill, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { IntInput, MoneyInput, Switch } from "./fields";

interface Form { sku: string; name: string; nameAr: string; price: number; taxRateId: string | null; active: boolean; items: Array<{ productId: string; qty: number }> }

function ComboDialog({ combo, data, onClose }: { combo: ComboView | null; data: CatalogAdminView; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const parts = data.products.filter((p) => ["addon", "fnb", "merch"].includes(p.kind) && p.active);
  const [f, setF] = useState<Form>(
    combo
      ? { sku: combo.sku, name: combo.name, nameAr: combo.nameAr ?? "", price: combo.price, taxRateId: combo.taxRateId, active: combo.active, items: combo.items.map((i) => ({ productId: i.productId, qty: i.qty })) }
      : { sku: "", name: "", nameAr: "", price: 0, taxRateId: data.taxRates[0]?.id ?? null, active: true, items: parts[0] ? [{ productId: parts[0].id, qty: 1 }] : [] },
  );
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const setItem = (i: number, patch: Partial<Form["items"][number]>) => setF((x) => ({ ...x, items: x.items.map((it, k) => (k === i ? { ...it, ...patch } : it)) }));
  const worth = f.items.reduce((s, i) => s + (parts.find((p) => p.id === i.productId)?.price ?? 0) * i.qty, 0);
  const save = useCatalogMutation(async () => {
    const body = { sku: f.sku.trim(), name: f.name.trim(), nameAr: f.nameAr.trim() || null, price: f.price, taxRateId: f.taxRateId, active: f.active, items: f.items };
    return combo ? api.put(`/catalog-admin/combos/${combo.id}`, body) : api.post("/catalog-admin/combos", body);
  });
  const dupes = new Set(f.items.map((i) => i.productId)).size !== f.items.length;
  const valid = f.sku.trim() && f.name.trim() && f.items.length > 0 && !dupes;
  async function submit() {
    try {
      await save.mutateAsync(undefined);
      toast(t("pricing.saved"), "success");
      onClose();
    } catch (e) {
      toastError(e);
    }
  }
  const cur = t("currency");
  return (
    <Modal open size="md" onClose={onClose} title={combo ? t("pricing.combo.editTitle") : t("pricing.combo.newTitle")}
      footer={<><button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button><button type="button" className="btn-primary" disabled={!valid || save.isPending} onClick={() => void submit()}>{t("pricing.save")}</button></>}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div><label className="label" htmlFor="cb-name">{t("pricing.product.name")}</label><input id="cb-name" className="field" value={f.name} maxLength={120} onChange={(e) => set("name", e.target.value)} /></div>
        <div><label className="label" htmlFor="cb-name-ar">{t("pricing.product.nameAr")}</label><input id="cb-name-ar" className="field" dir="rtl" value={f.nameAr} maxLength={120} onChange={(e) => set("nameAr", e.target.value)} /></div>
        <div><label className="label" htmlFor="cb-sku">{t("pricing.product.sku")}</label><input id="cb-sku" className="field num font-mono" value={f.sku} maxLength={40} onChange={(e) => set("sku", e.target.value.toUpperCase())} /></div>
        <div><label className="label" htmlFor="cb-price">{t("pricing.combo.price", { cur })}</label><MoneyInput id="cb-price" value={f.price} onChange={(v) => set("price", v ?? 0)} /></div>
        <div>
          <label className="label" htmlFor="cb-tax">{t("pricing.product.tax")}</label>
          <select id="cb-tax" className="field" value={f.taxRateId ?? ""} onChange={(e) => set("taxRateId", e.target.value || null)}>
            <option value="">{t("pricing.product.branchTax")}</option>
            {data.taxRates.map((r) => <option key={r.id} value={r.id}>{r.name} ({r.rate_bp / 100}%)</option>)}
          </select>
        </div>
      </div>
      <fieldset className="space-y-2 rounded-xl border border-gray-200 p-3">
        <legend className="px-1 text-[11px] font-black text-gray-600">{t("pricing.combo.parts")}</legend>
        <p className="text-[11px] font-semibold text-gray-500">{t("pricing.combo.partsHelp")}</p>
        {f.items.map((it, i) => (
          <div key={i} className="flex items-center gap-2" data-part={i + 1}>
            <select className="field flex-1" aria-label={t("pricing.combo.part", { n: i + 1 })} value={it.productId} onChange={(e) => setItem(i, { productId: e.target.value })}>
              {parts.map((p) => <option key={p.id} value={p.id}>{localName(p, locale)} ({money(p.price ?? 0)})</option>)}
            </select>
            <div className="w-20"><IntInput value={it.qty} min={1} max={100} label={t("pricing.combo.qty")} onChange={(n) => setItem(i, { qty: n })} /></div>
            <button type="button" className="btn-ghost !px-2 text-red-600" aria-label={t("pricing.combo.removePart", { n: i + 1 })} onClick={() => setF((x) => ({ ...x, items: x.items.filter((_, k) => k !== i) }))}>🗑</button>
          </div>
        ))}
        {dupes && <p className="text-[11px] font-bold text-red-600">{t("errors.DUPLICATE_PART")}</p>}
        <button type="button" className="btn-ghost text-xs" disabled={parts.length === 0} onClick={() => setF((x) => ({ ...x, items: [...x.items, { productId: parts.find((p) => !x.items.some((i) => i.productId === p.id))?.id ?? parts[0]!.id, qty: 1 }] }))}>＋ {t("pricing.combo.addPart")}</button>
        <p className="text-[11px] font-black text-cosmic-navy">{t("pricing.combo.worth", { amount: money(worth), cur })}{worth > f.price ? ` · ${t("pricing.combo.saving", { amount: money(worth - f.price), cur })}` : ""}</p>
      </fieldset>
      <Switch checked={f.active} onChange={(v) => set("active", v)}>{t("pricing.product.active")}</Switch>
    </Modal>
  );
}

export function CombosPanel({ data, canEdit }: { data: CatalogAdminView; canEdit: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [dialog, setDialog] = useState<{ combo: ComboView | null } | null>(null);
  const cur = t("currency");
  return (
    <section className="card space-y-3 p-4 sm:p-5" aria-label={t("pricing.tabs.combos")}>
      <SectionHeader
        title={`🎁 ${t("pricing.tabs.combos")}`}
        subtitle={t("pricing.combo.subtitle")}
        right={canEdit ? <button type="button" className="btn-primary !px-3 !py-1.5 text-xs" onClick={() => setDialog({ combo: null })}>＋ {t("pricing.combo.add")}</button> : undefined}
      />
      {data.combos.length === 0 ? (
        <Empty>{t("pricing.combo.none")}</Empty>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {data.combos.map((c) => (
            <div key={c.id} className={`rounded-2xl border p-3 ${c.active ? "border-gray-200 bg-gray-50" : "border-gray-200 bg-gray-100 opacity-70"}`} data-combo={c.sku}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-black text-cosmic-navy">{localName(c, locale)} <span className="num font-mono text-[10px] text-gray-400">{c.sku}</span></div>
                  <ul className="mt-1 text-[11px] font-semibold text-gray-600">
                    {c.items.map((i) => <li key={i.productId}>{i.qty} × {localName(i, locale)}</li>)}
                  </ul>
                </div>
                <div className="text-end">
                  <div className="num font-mono text-base font-black text-cosmic-purple">{money(c.price)} {cur}</div>
                  {c.saving > 0 && <Pill tone="green">{t("pricing.combo.saving", { amount: money(c.saving), cur })}</Pill>}
                </div>
              </div>
              {canEdit && <div className="mt-2 text-end"><button type="button" className="btn-ghost !px-2.5 !py-1 text-xs" onClick={() => setDialog({ combo: c })}>✏️ {t("pricing.edit")}</button></div>}
            </div>
          ))}
        </div>
      )}
      {dialog && <ComboDialog combo={dialog.combo} data={data} onClose={() => setDialog(null)} />}
    </section>
  );
}
